import { IconCopy } from '@tabler/icons-react';
import { ColorField } from '@/components/studio/ColorField';
import { formatColor, hexToOklch, type ColorFormat } from '@/lib/color-formats';
import { fitOklch } from '@/lib/color-input';
import { createColorScale } from '@/lib/palette-workflow';
import { PaletteStrip } from '@/components/studio/PaletteStrip';

export function ColorInspector({
  color,
  index,
  onChange,
  onCopy,
}: {
  color: string;
  index: number;
  onChange: (color: string) => void;
  onCopy: (text: string, label: string) => void;
}) {
  const oklch = hexToOklch(color);
  const channels = [
    { id: 'l' as const, name: 'Lightness', max: 1, step: 0.005, value: oklch.l },
    { id: 'c' as const, name: 'Chroma', max: 0.37, step: 0.002, value: oklch.c },
    { id: 'h' as const, name: 'Hue', max: 360, step: 1, value: oklch.h },
  ];
  return (
    <div className="color-inspector">
      <ColorField
        id="palette-selected"
        label={`Color ${index + 1}`}
        value={color}
        onChange={onChange}
        hint="Paste HEX, RGB, HSL, OKLab, or OKLCH."
      />
      <div className="color-channel-controls">
        {channels.map((item) => (
          <label className="studio-range-field" key={item.id}>
            <span>
              {`Color ${item.name.toLowerCase()}`}
              <output>
                {item.id === 'l'
                  ? `${(item.value * 100).toFixed(1)}%`
                  : item.id === 'h'
                    ? `${Math.round(item.value)}°`
                    : item.value.toFixed(3)}
              </output>
            </span>
            <input
              type="range"
              aria-label={`Selected color ${item.name.toLowerCase()}`}
              min={0}
              max={item.max}
              step={item.step}
              value={item.value}
              onChange={(event) => {
                const next = { ...oklch, [item.id]: Number(event.target.value) };
                onChange(fitOklch(next.l, next.c, next.h).hex);
              }}
            />
          </label>
        ))}
      </div>
      <div className="color-format-values">
        {(['rgb', 'hsl', 'oklch'] as ColorFormat[]).map((format) => {
          const value = formatColor(format, oklch.l, oklch.c, oklch.h);
          return (
            <div key={format}>
              <span>{format.toUpperCase()}</span>
              <code>{value}</code>
              <button
                type="button"
                className="studio-icon-button"
                aria-label={`Copy selected ${format} value`}
                onClick={() => onCopy(value, format.toUpperCase())}
              >
                <IconCopy />
              </button>
            </div>
          );
        })}
      </div>
      <div className="inspector-shades">
        <span className="studio-label">Shades & tints</span>
        <PaletteStrip
          colors={createColorScale(color).map((step) => step.hex)}
          compact
          onSelect={(hex) => onChange(hex)}
          label="Use shade"
        />
      </div>
    </div>
  );
}
